import api from '../api.js';
import db from '../../versa/db.js';
import { flatten, getTopIndex } from '../../versa/record.js';
import { loadOptions } from '../../versa/formset.js';
import * as types from '../types.js';
import { bindFormFieldTitleSync, syncFormFieldTitles } from '../utils/form-field-titles.js';
import { hydrateInputCancelReasons } from '../utils/input-cancel-reason.js';
import { notify } from '../../versa/msgbox.js';

function _parseSialNumInput(s) {
	const c = String(s ?? '').replace(/\./g, '').replace(',', '.');
	const n = parseFloat(c);
	return isFinite(n) ? n : 0;
}

function _fmtBRLInput(v) {
	return Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/** Retorna o elemento raiz do formulário #input, seja o próprio elemento ou o dialog que o contém. */
function _inputFormRoot(root) {
	if (!root) return document.querySelector('#input');
	return (root.id === 'input') ? root : (root.querySelector('#input') || root);
}

function _formatOrderPaymentSummary(src) {
	const count = Number(src?.payment_installment_count);
	let prazos = src?.payment_prazos;
	if (typeof prazos === 'string') {
		try { prazos = JSON.parse(prazos); } catch (_) { prazos = null; }
	}
	if (!Number.isFinite(count) || count <= 0 || !Array.isArray(prazos) || !prazos.length)
		return '';
	const parts = prazos.slice(0, count).map((d, i) => `${String(i + 1).padStart(2, '0')}:${d}d`);
	return `${count} parc. — ${parts.join(' · ')}`;
}

function _togglePaymentBlock(root, show) {
	const fr = _inputFormRoot(root);
	const block = fr?.querySelector('#in_payment_block');
	if (block)
		block.style.display = show ? '' : 'none';
}

/** #MT-6BF7122 — mostra/exige motivo quando status = Cancelada (80). */
async function _syncCancelReasonVisibility(formset, options = {})
{
	const root = _inputFormRoot(formset?.dialog?.getNode?.() || formset?.form?.node || document.querySelector('#input'));
	if (!root) return;
	const statusEl = root.querySelector('#status');
	const wrap = root.querySelector('#cancel_reason_wrap');
	const reasonEl = root.querySelector('#cancel_reason_id');
	const reasonField = formset?.form?.getFieldByName?.('cancel_reason_id');
	const companyField = formset?.form?.getFieldByName?.('company_id');
	const statusField = formset?.form?.getFieldByName?.('status');
	const companyId = companyField?.getValue?.() ?? root.querySelector('#company_id')?.value;
	const statusRaw = String(statusEl?.value || '').trim();
	const statusVal = statusField?.getValue?.();
	const isCanceled = String(statusVal ?? '') === '80'
		|| statusRaw === '80'
		|| /^cancelad/i.test(statusRaw);
	const savedReasonId = reasonField?.getValue?.();
	const displayNameHint = options.cancelReasonName || null;
	if (wrap)
		wrap.style.display = isCanceled ? '' : 'none';
	if (reasonEl)
	{
		if (isCanceled)
			reasonEl.setAttribute('required', 'required');
		else
		{
			reasonEl.removeAttribute('required');
			reasonEl.value = '';
			try { reasonField?.setValue?.(-1); } catch (_) { /* noop */ }
		}
	}
	if (!isCanceled || !companyId)
		return;
	// Popula a datalist direto (escopo do formulário), sem api.call/msgbox
	// e sem republicar company_id (isso reabria «Carregando...» e podia travar a tela).
	const inputId = formset?.form?.getFieldByName?.('id')?.getValue?.();
	const includeDisabled = inputId != null && inputId !== '' && Number(inputId) > 0;
	const rows = await hydrateInputCancelReasons(companyId, includeDisabled);
	const formSel = formset?.form?.selector || '#input';
	loadOptions('#cancel_reason_id', rows, 'id', ['name'], undefined, formSel);
	const rid = savedReasonId != null && savedReasonId !== '' && savedReasonId !== -1
		? savedReasonId
		: null;
	if (rid != null)
	{
		try
		{
			reasonField?.setValue?.(rid);
			reasonField?.refresh?.();
		}
		catch (_) { /* noop */ }
	}
	if (reasonEl && isCanceled && !String(reasonEl.value || '').trim())
	{
		const hit = rows.find((r) => Number(r.id) === Number(rid));
		const name = hit?.name || displayNameHint;
		if (name)
			reasonEl.value = name;
	}
	if (!rows.length)
		notify(
			'Não há motivos de cancelamento ativos para esta empresa. Cadastre em Catálogo → Motivos Cancelamento Entrada.',
			'Motivo cancelamento'
		);
}

function _resolveCancelReasonId(root)
{
	const el = root?.querySelector('#cancel_reason_id');
	if (!el) return null;
	const raw = String(el.value || '').trim();
	if (!raw) return null;
	if (/^\d+$/.test(raw)) return Number(raw);
	const opts = el.list?.options ? Array.from(el.list.options) : [];
	const byLabel = opts.find(o => String(o.label || o.textContent || '').trim() === raw);
	if (byLabel && /^\d+$/.test(String(byLabel.value)))
		return Number(byLabel.value);
	const byText = opts.find(o => String(o.textContent || '').trim() === raw);
	if (byText && /^\d+$/.test(String(byText.value)))
		return Number(byText.value);
	return null;
}

function recalcInputTotals(root) {
	if (!root) return;
	const fr = _inputFormRoot(root);
	const tbody = fr?.querySelector('.sial-items-scroll tbody');
	const totalEl = fr?.querySelector('#in-grand-total');
	if (!tbody) return;
	let grand = 0;
	tbody.querySelectorAll('tr.row').forEach((row) => {
		const qEl = row.querySelector('[id^="quantity-"]');
		if (!qEl) return;
		const n = (qEl.id.match(/\d+$/) || ['0'])[0];
		const qty   = _parseSialNumInput(qEl.value);
		const price = _parseSialNumInput(row.querySelector(`#unit_price-${n}`)?.value);
		const total = qty * price;
		const ti = row.querySelector(`#item_total-${n}`);
		if (ti) ti.value = (qty > 0 || price > 0) ? _fmtBRLInput(total) : '';
		grand += total;
	});
	if (totalEl)
		totalEl.textContent = grand > 0 ? `Total da Entrada: ${_fmtBRLInput(grand)}` : '';
}

function _enableInvoiceXmlDownload(b64)
{
	if (!b64) return;
	const anchor = document.querySelector('#download');
	if (!anchor) return;
	anchor.setAttribute('href', `data:text/xml;base64,${b64}`);
	anchor.removeAttribute('disabled');
}

/** #MT-3AD0979 — Entrada Concluída: formulário somente leitura (exceto Fechar e anexar XML). */
function _inputIsCompletedLocked(fs)
{
	const vals = fs?.form?.getValues?.() || {};
	return vals.edit_locked_by_completed === true || vals.edit_locked_by_completed === 1
		|| vals.edit_locked_by_completed === '1'
		|| String(vals.edit_locked_by_completed).toLowerCase() === 'true';
}

function applyInputCompletedEditLock(fs)
{
	const form = _inputFormRoot(fs?.dialog?.getNode?.()) || document.querySelector('#input');
	const banner = form?.querySelector('#in-lock-banner');
	if (!form) return;
	const locked = _inputIsCompletedLocked(fs);
	form.classList.toggle('input--edit-locked', locked);
	if (banner) banner.style.display = locked ? 'block' : 'none';
	const toolbar = form.querySelector('.in-footer span:first-child');
	const rowImgs = toolbar ? toolbar.querySelectorAll('img') : [];
	const saveBtn = form.querySelector('.bn_Enter');
	if (!locked)
	{
		form.querySelectorAll('[data-in-lock-disabled]').forEach((el) =>
		{
			el.removeAttribute('data-in-lock-disabled');
			el.removeAttribute('disabled');
		});
		rowImgs.forEach((img) => { img.style.pointerEvents = ''; img.style.opacity = ''; });
		if (saveBtn) saveBtn.style.display = '';
		return;
	}
	form.querySelectorAll('input, select, textarea, button').forEach((el) =>
	{
		if (el.type === 'hidden') return;
		if (el.tagName === 'BUTTON' && el.classList.contains('bn_Escape')) return;
		// XML da NF: permitido anexar mesmo com entrada concluída
		if (el.id === 'invoice_xml') return;
		el.setAttribute('data-in-lock-disabled', '1');
		el.setAttribute('disabled', 'disabled');
	});
	rowImgs.forEach((img) => { img.style.pointerEvents = 'none'; img.style.opacity = '0.45'; });
	if (saveBtn) {
		saveBtn.setAttribute('data-in-lock-disabled', '1');
		saveBtn.setAttribute('disabled', 'disabled');
		saveBtn.style.display = 'none';
	}
}

// Provisionado × Faturado × Pendente — dados do Pedido de Entrada vinculado
let _prov = {};       // product_id (str) → { ordered: int, received: int }
let _initialQty = {}; // row index (str) → qty salva no banco (edit mode)

function captureInitialQty(root) {
	_initialQty = {};
	_inputFormRoot(root)?.querySelectorAll('.sial-items-scroll tbody tr.row [id^="quantity-"]').forEach(el => {
		const n = (el.id.match(/\d+$/) || ['0'])[0];
		_initialQty[n] = _parseSialNumInput(el.value);
	});
}

function recalcProvisionedTotals(root) {
	const fr = _inputFormRoot(root);
	if (!fr) return;
	const tbody = fr.querySelector('.sial-items-scroll tbody');
	if (!tbody) return;
	const idVal = fr.querySelector('#id')?.value?.trim();
	const isEdit = !!(idVal && parseInt(idVal, 10) > 0);

	tbody.querySelectorAll('tr.row').forEach(row => {
		const qEl = row.querySelector('[id^="quantity-"]');
		if (!qEl) return;
		const n = (qEl.id.match(/\d+$/) || ['0'])[0];
		const pid = String(parseInt(row.querySelector(`#product_id-${n}`)?.value || '0', 10));
		const provEl  = row.querySelector(`#prov_qty-${n}`);
		const billEl  = row.querySelector(`#billed_qty-${n}`);
		const pendEl  = row.querySelector(`#pend_qty-${n}`);

		const provData = parseInt(pid, 10) > 0 ? _prov[pid] : null;
		if (!provData) {
			if (provEl)  provEl.value  = '';
			if (billEl)  billEl.value  = '';
			if (pendEl)  pendEl.value  = '';
			return;
		}

		const ordered       = provData.ordered;
		const received_api  = provData.received;
		const current_qty   = _parseSialNumInput(qEl.value);

		let faturada;
		if (isEdit) {
			// received_api já inclui este registro salvo; subtrai a qty original e adiciona a atual
			const initial_qty = _initialQty[n] ?? 0;
			const from_others = Math.max(0, received_api - initial_qty);
			faturada = from_others + current_qty;
		} else {
			// nova entrada: received_api conta outras entradas; soma a quantidade atual do form
			faturada = received_api + current_qty;
		}
		const pendente = Math.max(0, ordered - faturada);

		if (provEl)  provEl.value  = ordered  >= 0 ? String(ordered)  : '';
		if (billEl)  billEl.value  = faturada >= 0 ? String(faturada) : '';
		if (pendEl)  pendEl.value  = String(pendente);
	});
}

function _populateProvFromData(data) {
	_prov = {};
	if (data?.items) {
		for (const item of data.items) {
			if (item.product_id != null) {
				_prov[String(item.product_id)] = {
					ordered:  item.quantity_ordered  ?? 0,
					received: item.quantity_received ?? 0,
				};
			}
		}
	}
}

/** Destino / armazém / posição: só escolha na lista quando o tenant tem «Entrada com coletor». */
function applyInputCollectorDestinationReadonly(formset)
{
	const root = formset.dialog?.getNode?.() || document.querySelector('#input');
	if (!root)
		return;
	const dest = root.querySelector('#warehouse_company_id');
	const wh = root.querySelector('#warehouse_id');
	const loc = root.querySelector('#location_id');
	if (!dest || !wh || !loc)
		return;
	const cid = formset.form?.getFieldByName('company_id')?.getValue();
	const titles = {
		warehouse_company_id: 'Empresa do armazém de recebimento',
		warehouse_id: 'Armazém de recebimento',
		location_id: 'Posição de recebimento no armazém',
	};
	const setReadonly = (on) => {
		const hint = on
			? 'Selecione um item da lista (parâmetro «Entrada com coletor» ativo para esta empresa).'
			: '';
		for (const el of [dest, wh, loc])
		{
			el.readOnly = !!on;
			const base = titles[el.id] || '';
			el.title = hint ? `${base}. ${hint}` : base;
		}
	};
	if (cid == null || cid === '' || Number(cid) < 0)
	{
		setReadonly(false);
		return;
	}
	Promise.resolve(db.get('companies', cid))
		.then((c) => {
			const on = c && (c.input_collector_strict === true || c.input_collector_strict === '1' || c.input_collector_strict === 1);
			setReadonly(!!on);
		})
		.catch(() => setReadonly(false));
}

/** Após carregar registro: recarrega datalist de embalagem por linha e fabricante. */
async function resyncInputLinePackagings(formset)
{
	const root = formset.dialog?.getNode?.() || document.querySelector('#input');
	const form = formset.form;
	if (!root || !form)
		return;
	const company_id = form.getFieldByName('company_id')?.getValue();
	if (!company_id)
		return;
	const nodes = root.querySelectorAll('[id^="product_packaging_id-"]');
	for (const node of nodes)
	{
		const m = node.id.match(/(\d+)$/);
		if (!m)
			continue;
		const sel = `#product_packaging_id-${m[1]}`;
		const field = form.getFieldBySelector(sel);
		if (field && typeof field.actions === 'function')
			await field.actions();
	}
}

export default {

	html: `
		<style>
			#input.in-input-form .in-input-tail {
				flex-shrink: 0;
			}
			#input.in-input-form .in-input-header-scroll {
				flex-shrink: 0;
				overflow-x: hidden;
				padding-right: 0.2rem;
			}
			#input.in-input-form #company_id				{ width: 10rem;		}
			#input.in-input-form #id						{ width: 5rem; text-align: right;	}
			#input.in-input-form #order_id				{ width: 5rem; text-align: right;	}
			#input.in-input-form #order_type				{ width: 7rem; 		}
			#input.in-input-form #created_on				{ width: 9rem;		}
			#input.in-input-form #received_on				{ width: 9rem;		}
			#input.in-input-form #completed_at			{ width: 9rem;		}
			#input.in-input-form #warehouse_company_id	{ width: 10rem;		}
			#input.in-input-form #warehouse_id			{ width: 10rem;		}
			#input.in-input-form #location_id				{ width: 5rem;		}
			#input.in-input-form #program_id				{ width: 10rem;		}
			#input.in-input-form #invoice_number			{ width: 7.5rem;	}
			#input.in-input-form #invoice_series			{ width: 5rem;		}
			#input.in-input-form #invoice_xml				{ width: 13rem;		}
			#input.in-input-form #volumes					{ width: 5rem; text-align: right;	}
			#input.in-input-form #weight					{ width: 5rem; text-align: right;	}
			#input.in-input-form #status	 				{ width: 10rem;		}
			#input.in-input-form .in-grand-total {
				text-align: right; font-weight: 600; font-size: 0.88rem;
				padding: 0.3rem 0.25rem 0; color: var(--color-text, #222);
				flex-shrink: 0;
			}
			#input.in-input-form .sial-items-scroll .in-col-product { width: 20%; }
			#input.in-input-form .sial-items-scroll .in-col-pack { width: 8%; }
			#input.in-input-form .sial-items-scroll .in-col-mfr { width: 7%; }
			#input.in-input-form .sial-items-scroll .in-col-unit { width: 6%; }
			#input.in-input-form .sial-items-scroll .in-col-qty { width: 5%; }
			#input.in-input-form .sial-items-scroll .in-col-serial { width: 8%; }
			#input.in-input-form .sial-items-scroll .in-col-prov,
			#input.in-input-form .sial-items-scroll .in-col-bill,
			#input.in-input-form .sial-items-scroll .in-col-pend { width: 4%; }
			#input.in-input-form .sial-items-scroll .in-col-price { width: 7%; }
			#input.in-input-form .sial-items-scroll .in-col-total { width: 7%; }
			#input.in-input-form .sial-items-scroll .serial {
				min-width: 5.5rem;
				background: #fffbeb;
				border-color: #f59e0b;
			}
			#input.in-input-form .in-footer {
				display: flex;
				justify-content: space-between;
				align-items: center;
				flex-shrink: 0;
				margin-top: 0.45rem;
				padding-top: 0.65rem;
				border-top: 1px solid var(--color-border, #ccc);
				gap: 0.75rem;
				flex-wrap: wrap;
			}
			#in-lock-banner { margin: 0 0 0.75rem 0; padding: 0.5rem 0.65rem; background: #f1f5f9; border: 1px solid #cbd5e1; border-radius: 4px; color: #475569; }
			#input.in-input-form.input--edit-locked label { color: #94a3b8; }
		</style>
		<form id="input" class="form in-input-form sial-modal-form" autocomplete="off">
			<input type="hidden" id="edit_locked_by_completed" value="0">
			<p id="in-lock-banner" class="fsxx" style="display:none">Esta Entrada está <strong>Concluída</strong>: não é possível alterar cabeçalho, quantidades nem valores. Ainda é possível <strong>anexar o XML da NF</strong>. Para reabrir os demais campos, use a operação <strong>Estornar</strong>.</p>
			<datalist id="companies">
			</datalist>
			<datalist id="warehouses">
			</datalist>
			<datalist id="locations">
			</datalist>
			<datalist id="products">
			</datalist>
			<datalist id="units">
			</datalist>
			<datalist id="programs">
			</datalist>
			<div class="in-input-header-scroll">
			<div class="fsb">
				<label title="Empresa detentora do registro">Empresa<sup>*</sup>
					<input id="company_id" type="text" list="companies" required>
				</label>
				<label title="Identificador da entrada">Entrada
					<input id="id" type="text" disabled>
				</label>
				<label title="Número do pedido vinculado">Pedido<sup>*</sup>
					<input id="order_id" type="text" required>
				</label>
				<label title="Tipo do pedido vinculado">Tipo<sup>*</sup>
					<input id="order_type" type="text" list="order_types" value="I" required>
				</label>
				<label title="Data e hora de criação da entrada">Criação
					<input id="created_on" type="text" disabled>
				</label>
				<label title="Data e hora de recebimento da mercadoria">Recebimento
					<input id="received_on" type="text" disabled>
				</label>
				<label title="Data e hora em que a entrada foi concluída (endereçamento)">Conclusão
					<input id="completed_at" type="text" disabled>
				</label>
				<label title="Status da entrada">Status<sup>*</sup>
					<input id="status" type="text" list="input_status" value="10" required
						title="Não use Conferida/Concluída aqui: Conferida vem da Conferência de Entrada; Concluída do Endereçamento. Alterar só estes status no formulário é bloqueado pela API.">
				</label>
				<label id="cancel_reason_wrap" title="Obrigatório quando Status = Cancelada" style="display:none">Motivo cancelamento<sup>*</sup>
					<datalist id="input_cancel_reasons"></datalist>
					<input id="cancel_reason_id" type="text" list="input_cancel_reasons" autocomplete="off">
				</label>
			</div>
			<div class="fsb">
				<label title="Empresa do armazém de recebimento">Destino
					<input id="warehouse_company_id" type="text" list="companies">
				</label>
				<label title="Armazém de recebimento">Armazém
					<input id="warehouse_id" type="text" list="warehouses">
				</label>
				<label title="Posição de recebimento no armazém">Posição
					<input id="location_id" type="text" list="locations">
				</label>
				<label title="Programa da entrada (todos os lotes desta entrada ficarão nesse programa)">Programa
					<input id="program_id" type="text" list="programs">
				</label>
				<label title="Número da nota fiscal">Nota Fiscal
					<input id="invoice_number" type="text">
				</label>
				<label title="Série da nota fiscal (ex.: 1, A)">Série
					<input id="invoice_series" type="text" maxlength="10" placeholder="1">
				</label>
				<label class="file" ><a id="download" download="invoice.xml">XML</a>
					<input id="invoice_xml" type="file" accept=".xml, text/xml" title="Importar XML da nota fiscal">
				</label>
				<label title="Número de volumes">Volumes
					<input id="volumes" type="text">
				</label>
				<label title="Peso total dos volumes">Peso
					<input id="weight" type="text">
				</label>
			</div>
			<div class="fsb in-payment-from-order" id="in_payment_block" style="display:none;max-width:48rem;">
				<label title="Condição de pagamento do Pedido de Entrada (somente leitura)">Cond. pagamento (pedido)
					<input id="order_payment_summary" type="text" disabled style="min-width:28rem;">
				</label>
			</div>
			</div>
			<div class="sial-items-panel">
				<div class="sial-items-title">Itens da entrada</div>
				<div class="sial-items-scroll">
					<table>
						<thead>
							<tr>
								<th class="sial-col-chk"></th>
								<th class="in-col-product">Produto<sup>*</sup></th>
								<th class="in-col-pack">Embalagem</th>
								<th class="in-col-mfr">Fabricante</th>
								<th class="in-col-unit">Unidade<sup>*</sup></th>
								<th class="in-col-qty tar">Qtde<sup>*</sup></th>
								<th class="in-col-serial" title="Opcional — letras e números, até 20">Serial</th>
								<th class="in-col-prov tar" title="Quantidade fechada no Pedido de Entrada">Qtd Prov.</th>
								<th class="in-col-bill tar" title="Quantidade já faturada (acumulado de todas as entradas ativas)">Qtd Fat.</th>
								<th class="in-col-pend tar" title="Quantidade pendente de entrega (Provisionada - Faturada)">Qtd Pend.</th>
								<th class="in-col-price tar">Preço Unit<sup>*</sup></th>
								<th class="in-col-total tar">Valor Total</th>
							</tr>
						</thead>
						<tbody>
							<tr class="row" data-size="10">
								<td class="sial-col-chk">
									<input id="checkbox-0" type="checkbox">
								</td>
								<td title="Produto">
									<input id="product_id-0" class="product" type="text" list="products">
								</td>
								<td title="Embalagem (P/S/T do produto)">
									<datalist id="packagings-0"></datalist>
									<input id="product_packaging_id-0" class="packaging" type="text" list="packagings-0" placeholder="—" autocomplete="off">
								</td>
								<td title="Fabricante da embalagem (automático)">
									<input id="manufacturer_display-0" class="manufacturer_display" type="text" readonly tabindex="-1" autocomplete="off">
								</td>
								<td title="Unidade de compra">
									<input id="unit_id-0" class="unit" type="text" list="units" required>
								</td>
								<td class="tar" title="Quantidade">
									<input id="quantity-0" class="quantity" type="text" required>
								</td>
								<td title="Serial opcional (letras e números, até 20)">
									<input id="serial-0" class="serial" type="text" maxlength="20" pattern="[A-Za-z0-9]*" placeholder="Opcional" autocomplete="off">
								</td>
								<td class="tar" title="Quantidade provisionada no Pedido de Entrada">
									<input id="prov_qty-0" class="prov_qty" type="text" readonly tabindex="-1">
								</td>
								<td class="tar" title="Quantidade já faturada (acumulado de todas as entradas ativas do pedido)">
									<input id="billed_qty-0" class="billed_qty" type="text" readonly tabindex="-1">
								</td>
								<td class="tar" title="Quantidade pendente de entrega (Provisionada - Faturada)">
									<input id="pend_qty-0" class="pend_qty" type="text" readonly tabindex="-1">
								</td>
								<td class="tar" title="Preço unitário de compra">
									<input id="unit_price-0" class="price" type="text" required>
								</td>
								<td class="tar" title="Valor total do item (qtde × preço unit.)">
									<input id="item_total-0" class="item_total" type="text" readonly tabindex="-1">
								</td>
							</tr>
						</tbody>
					</table>
				</div>
			</div>
			<div class="in-input-tail">
				<div id="in-grand-total" class="in-grand-total"></div>
			</div>
			<div class="in-footer">
				<span>
					<img class="bn_Invert" src="img/check.svg" title="Inverter caixas de seleção">
					<img class="bn_Clear" src="img/clear.svg" title="Apagar conteúdo das linhas selecionadas">
					<img class="bn_Insert" src="img/add.svg" title="Anexar uma nova linha">
					<img class="bn_Remove" src="img/remove.svg" title="Remover linhas selecionadas">
					<img class="bn_MoveUp" src="img/up.svg" title="Mover linhas selecionadas para cima">
					<img class="bn_MoveDn" src="img/down.svg" title="Mover linhas selecionadas para baixo">
				</span>
				<span>
					<button class='bn_Escape button'>Fechar</button>
					<button class='bn_Enter button'>Salvar</button>
				</span>
			</div>
		</form>
	`,

	form: 
	{
		selector: '#input',
	afterEditSetValues(formset, currentData)
	{
		if (formset?.hasTables?.())
			formset.setTableHeight(10);
		applyInputCompletedEditLock(formset);
		setTimeout(() => syncFormFieldTitles(_inputFormRoot(formset.dialog?.getNode?.())), 50);
		// Dispara observers de destino/armazém para popular datalist de posição (como em Saídas).
		const whCo = formset.form.getFieldBySelector('#warehouse_company_id');
		const wh = formset.form.getFieldBySelector('#warehouse_id');
		const whCoVal = whCo?.getValue?.();
		if (whCo && typeof whCo.publish === 'function' && whCoVal != null && whCoVal !== '')
			whCo.publish('change', whCoVal);
		const whVal = wh?.getValue?.();
		if (wh && typeof wh.publish === 'function' && whVal != null && whVal !== '')
			wh.publish('change', whVal);
		setTimeout(() => { resyncInputLinePackagings(formset).catch(() => {}); }, 80);
		setTimeout(() => applyInputCollectorDestinationReadonly(formset), 0);
		const root = formset.dialog?.getNode?.() || document.querySelector('#input');
		setTimeout(() => {
			recalcInputTotals(root);
			applyInputCompletedEditLock(formset);
			_syncCancelReasonVisibility(formset, {
				cancelReasonName: currentData?.cancel_reason_name,
			});
		}, 0);
		// Carregar provisionado/faturado ao editar entrada existente (#MT-2B00548)
		const fr = _inputFormRoot(root);
		const company_id = fr?.querySelector('#company_id')?.value;
		const order_id   = fr?.querySelector('#order_id')?.value;
		const order_type = fr?.querySelector('#order_type')?.value;
		if (company_id && order_id && order_type === 'I') {
			api.call(api.select_pending_input_items, company_id, Number(order_id))
				.then(data => {
					_populateProvFromData(data);
					captureInitialQty(root);
					recalcProvisionedTotals(root);
					const summary = _formatOrderPaymentSummary(data);
					const sumEl = fr?.querySelector('#order_payment_summary');
					if (sumEl && summary)
						sumEl.value = summary;
					_togglePaymentBlock(fr, !!summary || !!(sumEl && sumEl.value));
					applyInputCompletedEditLock(formset);
				})
				.catch(() => {});
		} else {
			_togglePaymentBlock(fr, false);
		}
	},
		init(formset)	// Must not be async as init seq must be preserved
		{
			formset.loadList('#companies', 'companies', { labels: ['nick'] });
			this.getFieldByName('order_id').subscribe(['change'], this.checkCondition.bind(formset), false);	// once = false
			this.getFieldByName('order_type').subscribe(['change'], this.checkCondition.bind(formset), false);	// once = false
			this.getFieldByName('company_id').subscribe(['change'], () => applyInputCollectorDestinationReadonly(formset), false);
			this.getFieldByName('invoice_xml').subscribe(['change'], this.enableDownload.bind(formset), false);	// once = false
			// status precisa de types.pubsub() nos fields — senão getFieldByName devolve objeto sem subscribe
			const statusField = this.getFieldByName('status');
			if (statusField && typeof statusField.subscribe === 'function')
				statusField.subscribe(['change'], () => _syncCancelReasonVisibility(formset), false);
			this.getFieldByName('company_id').subscribe(['change'], () => _syncCancelReasonVisibility(formset), false);
			document.querySelector('#invoice_xml').addEventListener('change', this.fillItemsFromXML.bind(formset));
			Object.getPrototypeOf(this).init.call(this);
			setTimeout(() => applyInputCollectorDestinationReadonly(formset), 0);
			setTimeout(() => _syncCancelReasonVisibility(formset), 0);
			const root = formset.dialog?.getNode?.()?.querySelector('#input') || document.querySelector('#input');
			bindFormFieldTitleSync(root);
			if (root)
			{
				const tbl = root.querySelector('.sial-items-scroll table');
				if (tbl)
					tbl.addEventListener('input', () => {
						recalcInputTotals(root);
						recalcProvisionedTotals(root);
					});
			}
		},
		checkCondition(ev, publisher, value)
		{
			let company_id = this.form.getFieldByName('company_id').getValue();
			let order_id = this.form.getFieldByName('order_id').getValue();
			let order_type = this.form.getFieldByName('order_type').getValue();
			let key = String([company_id, order_type, order_id])
			if (company_id < 0 || order_type < 0 || !order_id || key == this.last_key)
				return;
			let is_edit = this.form.getFieldByName('id').getValue();
			if (!is_edit || (is_edit && this.last_key))		// If insert (no id) fill it now; If edit (has id) fill it only after 2nd change (has last_key)
				this.form.fillItems.call(this, company_id, order_type, order_id);
			this.last_key = key;
		},
		async fillItems(company_id, order_type, order_id)
		{
			let items;
			let invoice_number;
			let paymentSummary = '';
			switch (order_type)
			{
			case 'I':
				{
					const pending = await api.call(api.select_pending_input_items, company_id, order_id);
					if (pending && typeof pending === 'object' && !Array.isArray(pending) && pending.items)
					{
						items = pending.items;
						invoice_number = pending.nf_number;
						_populateProvFromData(pending);
						_initialQty = {};
						paymentSummary = _formatOrderPaymentSummary(pending);
					}
					else
					{
						items = pending;
						_prov = {};
					}
				}
				break;
				case 'O':
					let order = await api.call(api.select_output_order, company_id, order_id);
					items = order.items;
					break;
			}
		if (items)
		{
			this.clearAllRows();
			let record = flatten({ 'items': items }, 'items');
			if (order_type === 'I' && invoice_number != null && String(invoice_number).trim())
			{
				const cur = this.form.getFieldByName('invoice_number')?.getValue();
				if (cur == null || String(cur).trim() === '')
					record.invoice_number = String(invoice_number).trim();
			}
			if (order_type === 'I')
				record.order_payment_summary = paymentSummary;
			this.appendRows(getTopIndex(record) - this.getEnabledRowsIndices().length + 1);
			this.form.setValues(record);
			this.form.refresh();
			const fillRoot = this.dialog?.getNode?.() || document.querySelector('#input');
			_togglePaymentBlock(fillRoot, order_type === 'I' && !!paymentSummary);
			setTimeout(() => recalcProvisionedTotals(fillRoot), 50);
		}
		},
		enableDownload(ev, publisher, value)
		{
			_enableInvoiceXmlDownload(value);
		},
		fillItemsFromXML(ev)
		{
			const file = ev?.target?.files?.[0];
			if (!file) return;
			const locked = _inputIsCompletedLocked(this);
			let reader = new FileReader();
			reader.onloadend = async () =>
			{
				const b64 = reader.result.slice(reader.result.indexOf(',') + 1);
				if (locked)
				{
					const companyId = this.form.getFieldByName('company_id').getValue();
					const inputId = this.form.getFieldByName('id').getValue();
					if (!companyId || !inputId)
					{
						const { notify } = await import('../../versa/msgbox.js');
						notify('Salve a entrada antes de anexar o XML.', 'Aviso');
						return;
					}
					try
					{
						const resp = await api.call(api.patch_input, companyId, inputId, {
							invoice_xml: b64,
						});
						if (resp == null) return;
						_enableInvoiceXmlDownload(b64);
						// Atualiza nº/série na tela se a API preencheu (campos desabilitados)
						try
						{
							const fresh = await api.call(api.select_input, companyId, inputId);
							if (fresh)
							{
								const num = fresh.invoice_number;
								const ser = fresh.invoice_series;
								const numEl = document.querySelector('#invoice_number');
								const serEl = document.querySelector('#invoice_series');
								if (numEl && num != null && String(num).trim() !== '')
									numEl.value = String(num);
								if (serEl && ser != null && String(ser).trim() !== '')
									serEl.value = String(ser);
							}
						}
						catch (_) { /* anexo já gravado */ }
						const { notify } = await import('../../versa/msgbox.js');
						notify('XML da NF anexado na entrada concluída.', 'Entrada');
					}
					catch (e)
					{
						const { notify } = await import('../../versa/msgbox.js');
						notify(e?.message || String(e), 'Erro');
					}
					return;
				}
				let data = {
					'company_id': this.form.getFieldByName('company_id').getValue(),
					'order_type': this.form.getFieldByName('order_type').getValue(),
					'order_id': this.form.getFieldByName('order_id').getValue(),
					'file': b64
				};
				let result = await api.call(api.import_xml, data);
				if (!result) return;
				_enableInvoiceXmlDownload(b64);
				this.form.setValues(flatten(result, 'items'));
				this.form.refresh();
				// Recalcular totais e colunas derivadas após importação XML (#MT-AFDC849 / #MT-2B00548)
				const xmlRoot = this.dialog?.getNode?.() || document.querySelector('#input');
				setTimeout(() => {
					recalcInputTotals(xmlRoot);
					recalcProvisionedTotals(xmlRoot);
				}, 50);
			};
			reader.readAsDataURL(file);
		}
	},
	
	fields: [
		{ properties: { selector: '#edit_locked_by_completed' }, types: [types.pubsub()] },
		{ properties: { selector: '#company_id' }, types: [types.pubsub(), types.datalist()] },
		{ properties: { selector: '#id' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#order_id' }, types: [types.pubsub(), types.number(0, 2)] },
		{ properties: { selector: '#order_type' }, types: [types.pubsub(), types.opt_order_type(), types.datalist()] },
		{ properties: { selector: '#created_on' }, types: [types.datetime()] },
		{ properties: { selector: '#received_on' }, types: [types.datetime()] },
		{ properties: { selector: '#completed_at' }, types: [types.datetime()] },
		{ properties: { selector: '#warehouse_company_id' }, types: [types.pubsub(), types.datalist()] },	
		{ properties: { selector: '#warehouse_id' }, types: [types.pubsub(), types.datalist(), types.observer('warehouse_company_id'), types.obs_load_options('warehouses')] },
		{ properties: { selector: '#location_id' }, types: [types.pubsub(), types.datalist(), types.observer('warehouse_id'), types.obs_load_options('locations', { labels: ['address'], sep: '-', key: 'warehouse_company_id' })] },
		{ properties: { selector: '#program_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('programs', { labels: ['name'] })] },
		{ properties: { selector: '#invoice_number' }, types: [] },
		{ properties: { selector: '#invoice_series' }, types: [] },
		{ properties: { selector: '#invoice_xml' }, types: [types.pubsub(), types.file()] },
		{ properties: { selector: '#volumes' }, types: [types.number(0, 2)] },
		{ properties: { selector: '#weight' }, types: [types.number(3)] },
		{ properties: { selector: '#order_payment_summary' }, types: [] },
		{ properties: { selector: '#status' }, types: [types.pubsub(), types.opt_input_status(), types.datalist()] },
		{ properties: { selector: '#cancel_reason_id' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('input_cancel_reasons', { labels: ['name'] })] },
		{ properties: { selector: '#checkbox-0' }, types: [types.checkbox()] },
		{ properties: { selector: '#product_id-0' }, types: [types.pubsub(), types.datalist(), types.observer('company_id'), types.obs_load_options('products', { labels: ['sku', 'name'] })] },
		{ properties: { selector: '#product_packaging_id-0' }, types: [types.pubsub(), types.datalist(), types.observer(['company_id', 'product_id-0']), types.obs_input_line_packaging()] },
		{ properties: { selector: '#manufacturer_display-0' }, types: [] },
		{ properties: { selector: '#unit_id-0' }, types: [types.pubsub(), types.datalist(), types.observer(['company_id', 'product_id-0']), types.obs_unit(1)] },
		{ properties: { selector: '#quantity-0' }, types: [types.number(0, 2)]},
		{ properties: { selector: '#serial-0' }, types: [types.caps()] },
		{ properties: { selector: '#prov_qty-0' }, types: [] },
		{ properties: { selector: '#billed_qty-0' }, types: [] },
		{ properties: { selector: '#pend_qty-0' }, types: [] },
		// Até 10 casas (MS/SUS), sem completar zeros visuais desnecessários.
		{ properties: { selector: '#unit_price-0' }, types: [types.number(10, 0, ',', '.', false)] },
		{ properties: { selector: '#item_total-0' }, types: [] },
	]

};

